import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { Box, Button, Divider, Flex, HStack, Heading, Icon, PinInput, PinInputField, Spinner, Switch, Text, useToast, VStack } from "@chakra-ui/react";
import { FiShield, FiKey, FiSmartphone, FiMail } from "react-icons/fi";
import {
  AuthorizationModel,
  twoFactorSession,
  twoFactorVerify,
  twoFactorSendEmail,
  twoFactorPasskeyOptions,
  twoFactorPasskeyVerify
} from "@lubbezposrednio-frontend/core";
import { authenticatePasskey } from "../../ProfileView/components/webauthnClient";

type Method = "passkey" | "totp" | "push" | "email";

interface TwoFactorPanelProps {
  authorization: AuthorizationModel;
}

export const TwoFactorPanel: React.FC<TwoFactorPanelProps> = observer(({ authorization }) => {
  const challenge = authorization.twoFactorChallenge;
  const navigate = useNavigate();
  const toast = useToast();

  const methods = challenge?.methods ?? {};
  const rememberOffered = challenge?.rememberOffered !== false; // domyślnie tak; przy „zawsze pytaj" backend zwraca false

  const [method, setMethod] = useState<Method>((challenge?.channel as Method) ?? "email");
  const [rememberDevice, setRememberDevice] = useState(true);
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [resendIn, setResendIn] = useState(challenge?.channel === "email" ? 30 : 0);
  const rememberRef = useRef(rememberDevice);
  rememberRef.current = rememberDevice;

  const codeFromApp = method === "totp";

  // Odpytywanie sesji — zatwierdzenie na innym urządzeniu (push) samo dokończy logowanie.
  useEffect(() => {
    if (!challenge || method !== "push") return;
    let active = true;
    (async () => {
      while (active) {
        try {
          const res = await twoFactorSession(challenge.token, rememberRef.current);
          if (res.data?.access_token) {
            authorization.finishTwoFactor(res.data, navigate);
            return;
          }
        } catch (e) {
          if ((e as { response?: { status?: number } })?.response?.status === 404) {
            authorization.clearTwoFactorChallenge();
            toast({ title: "Sesja logowania wygasła — zaloguj się ponownie.", status: "info", isClosable: true });
            return;
          }
        }
        await new Promise(r => setTimeout(r, 3000));
      }
    })();
    return () => {
      active = false;
    };
  }, [method]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  if (!challenge) return null;

  const verify = async (value: string) => {
    setVerifying(true);
    try {
      const res = await twoFactorVerify(challenge.token, value, rememberDevice);
      authorization.finishTwoFactor(res.data, navigate);
    } catch {
      toast({ title: "Nieprawidłowy lub wygasły kod", status: "error", isClosable: true });
      setCode("");
    } finally {
      setVerifying(false);
    }
  };

  // Passkey jako 2FA — ceremonia WebAuthn (Touch ID / klucz) → weryfikacja → sesja.
  const doPasskey = async () => {
    setPasskeyBusy(true);
    try {
      const { data } = await twoFactorPasskeyOptions(challenge.token);
      const credential = await authenticatePasskey(data.options);
      const res = await twoFactorPasskeyVerify(challenge.token, data.webauthn_token, credential, rememberDevice);
      authorization.finishTwoFactor(res.data, navigate);
    } catch (e) {
      const err = e as { name?: string; message?: string; response?: { data?: { errors?: string[] } } };
      if (err?.name !== "NotAllowedError") {
        toast({ title: "Passkey", description: err?.response?.data?.errors?.[0] ?? err?.message ?? "Nie udało się.", status: "error", duration: 8000, isClosable: true });
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      await twoFactorSendEmail(challenge.token);
      setResendIn(30);
    } catch {
      toast({ title: "Nie udało się wysłać kodu — odczekaj chwilę i spróbuj ponownie.", status: "error", isClosable: true });
    } finally {
      setSending(false);
    }
  };

  // Przełączenie metody (z „Wypróbuj inną metodę").
  const switchTo = async (m: Method) => {
    setCode("");
    setMethod(m);
    if (m === "email") await sendEmail();
    if (m === "passkey") doPasskey();
  };

  // Inne dostępne metody (do przełączenia) — bez bieżącej; push pomijamy (nie da się łatwo wywołać ponownie).
  const allMethods: { id: Method; label: string; icon: typeof FiKey }[] = [
    { id: "passkey", label: "Passkey (odcisk / klucz)", icon: FiKey },
    { id: "totp", label: "Kod z aplikacji authenticator", icon: FiSmartphone },
    { id: "email", label: "Kod na e-mail", icon: FiMail }
  ];
  const others = allMethods.filter(o => o.id !== method && (methods as Record<string, boolean | undefined>)[o.id]);

  return (
    <Flex minH="100vh" align="center" justify="center" bg="rgba(67,0,152,0.03)" p="20px">
      <Box bg="white" borderRadius="24px" boxShadow="0 14px 50px rgba(67,0,152,0.12)" p={{ base: "28px", md: "40px" }} w="100%" maxW="440px">
        <VStack spacing="8px" mb="24px">
          <Flex w="64px" h="64px" borderRadius="18px" bg="rgba(67,0,152,0.08)" align="center" justify="center">
            <Icon as={FiShield} boxSize="30px" color="primary" />
          </Flex>
          <Heading fontSize="20px" fontWeight="800" color="primary">Potwierdź, że to Ty</Heading>
          {challenge.emailHint && (
            <Text fontSize="13px" color="gray.500" textAlign="center">Drugi składnik dla {challenge.emailHint}</Text>
          )}
        </VStack>

        {method === "passkey" && (
          <VStack align="stretch" spacing="14px">
            <Box bg="rgba(67,0,152,0.04)" borderRadius="14px" p="16px">
              <HStack mb="6px"><Icon as={FiKey} color="primary" /><Text fontWeight="700" color="gray.700">Potwierdź passkeyem</Text></HStack>
              <Text fontSize="13px" color="gray.500">Odciskiem, twarzą lub kluczem sprzętowym — najszybciej i bez kodu.</Text>
            </Box>
            <Button bg="primary" color="white" borderRadius="12px" fontWeight="700" leftIcon={<FiKey />} isLoading={passkeyBusy} onClick={doPasskey} _hover={{ bg: "#360078" }}>
              Użyj passkey
            </Button>
          </VStack>
        )}

        {method === "push" && (
          <Box bg="rgba(67,0,152,0.04)" borderRadius="14px" p="16px">
            <HStack mb="6px"><Spinner size="sm" color="primary" /><Text fontWeight="700" color="gray.700">Zatwierdź w aplikacji</Text></HStack>
            <Text fontSize="13px" color="gray.500">Wysłaliśmy prośbę na Twoje zaufane urządzenia. Potwierdź — dokończy się tutaj automatycznie.</Text>
          </Box>
        )}

        {(method === "totp" || method === "email") && (
          <VStack align="stretch" spacing="14px">
            <Text fontWeight="700" color="gray.700">{codeFromApp ? "Wpisz kod z aplikacji" : "Wpisz kod z e-maila"}</Text>
            {codeFromApp && (
              <Text fontSize="13px" color="gray.500" mt="-6px">Otwórz aplikację authenticator (Google Authenticator / 1Password / Authy) i przepisz aktualny 6-cyfrowy kod.</Text>
            )}
            <HStack justify="space-between">
              <PinInput otp type="number" value={code} onChange={setCode} onComplete={verify} size="lg" focusBorderColor="primary">
                {[0, 1, 2, 3, 4, 5].map(i => <PinInputField key={i} flex="1" fontWeight="800" />)}
              </PinInput>
            </HStack>
            <Button bg="primary" color="white" borderRadius="12px" fontWeight="700" isLoading={verifying} isDisabled={code.length < 6} onClick={() => verify(code)} _hover={{ bg: "#360078" }}>
              Potwierdź
            </Button>
            {method === "email" && (
              <Button variant="link" color={resendIn > 0 ? "gray.400" : "primary"} fontWeight="600" fontSize="14px" isDisabled={resendIn > 0 || sending} onClick={sendEmail}>
                {resendIn > 0 ? `Wyślij ponownie za ${resendIn}s` : "Wyślij kod ponownie"}
              </Button>
            )}
          </VStack>
        )}

        {others.length > 0 && (
          <Box mt="20px">
            <Divider mb="14px" />
            <Text fontSize="12px" color="gray.400" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" mb="8px">Wypróbuj inną metodę</Text>
            <VStack align="stretch" spacing="8px">
              {others.map(o => (
                <Button key={o.id} variant="outline" borderColor="rgba(67,0,152,0.18)" color="gray.700" borderRadius="12px" fontWeight="600" fontSize="14px" justifyContent="flex-start" leftIcon={<Icon as={o.icon} color="primary" />} isDisabled={sending || passkeyBusy} onClick={() => switchTo(o.id)} _hover={{ bg: "rgba(67,0,152,0.04)" }}>
                  {o.label}
                </Button>
              ))}
            </VStack>
          </Box>
        )}

        {rememberOffered && (
          <Flex align="center" justify="space-between" mt="20px">
            <Box pr="12px">
              <Text fontSize="14px" fontWeight="600" color="gray.700">Zapamiętaj to urządzenie</Text>
              <Text fontSize="12px" color="gray.400">Przez 30 dni nie poprosimy tu o drugi składnik.</Text>
            </Box>
            <Switch colorScheme="purple" isChecked={rememberDevice} onChange={e => setRememberDevice(e.target.checked)} />
          </Flex>
        )}

        <Button variant="link" color="gray.400" fontWeight="600" fontSize="14px" mt="18px" w="100%" onClick={() => authorization.clearTwoFactorChallenge()}>
          Anuluj
        </Button>
      </Box>
    </Flex>
  );
});
